(function () {
  if (window.BullnoseCalculatorMath) return;

  window.BullnoseCalculatorMath = {
    /**
     * @param {number} enteredFt
     * @param {number} rate pieces per linear foot
     * @param {number} overagePercent e.g. 20 for 20%
     * @returns {number} whole pieces (ceil), or 0 if input invalid
     */
    requiredPieces(enteredFt, rate, overagePercent) {
      const ft = parseFloat(enteredFt);
      const r = parseFloat(rate);
      const ov = parseFloat(overagePercent);
      if (!Number.isFinite(ft) || ft <= 0) return 0;
      if (!Number.isFinite(r) || r <= 0) return 0;
      const overage = Number.isFinite(ov) && ov > 0 ? ov : 0;
      const effectiveFt = ft * (1 + overage / 100);
      return Math.ceil(effectiveFt * r);
    },

    summaryText(pieces, enteredFt) {
      const p = parseInt(pieces, 10);
      const y = parseFloat(enteredFt);
      if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(y) || y <= 0) {
        return '';
      }
      const pieceWord = p === 1 ? 'piece' : 'pieces';
      return `Total: You will need ${p} ${pieceWord} for ${y} linear feet.`;
    }
  };
})();

(function () {
  if (typeof window.BullnoseCalculatorClass !== 'undefined') return;

  window.BullnoseCalculatorClass = class BullnoseCalculator {
    constructor() {
      this.root = document.querySelector('[data-bullnose-calculator="1"]');
      if (!this.root) return;
      this.root.dataset.bullnoseInitialized = '1';
      this.bindEvents();
      this.syncPiecePrice();
    }

    // Look up the current live calculator instance (may have been swapped on section reload).
    static get current() {
      return window.BullnoseCalculator || null;
    }

    getRate() {
      return parseFloat(this.root.getAttribute('data-pieces-per-ft') || '0');
    }

    getOveragePercent() {
      const hidden = this.root.querySelector('.js-bullnose-overage-value');
      const parsed = parseFloat(hidden?.value || '0');
      return Number.isFinite(parsed) ? parsed : 0;
    }

    getEnteredFt() {
      const input = this.root.querySelector('#bullnose_ft_need');
      return parseFloat(input?.value || '');
    }

    getMinQty() {
      const qtyInput = document.querySelector(
        '.quantity-selector-container input[name="quantity"], .product__quantity input[name="quantity"]'
      );
      const minAttr = parseInt(qtyInput?.getAttribute('min') || '1', 10);
      return Number.isFinite(minAttr) && minAttr > 0 ? minAttr : 1;
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney && window.theme?.settings?.moneyFormat) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    }

    getPiecePriceCents() {
      const variant = this.getCurrentVariant();
      const priceEl = this.root.querySelector('.js-bullnose-piece-price');

      // Prefer live WPD-updated attribute when it belongs to the current variant. WPD writes
      // `data-wpd-variant-price` (and matches `data-wpd-variant-id`) after processing the
      // trade/wholesale price — use it so we don't fall back to retail from window.meta.
      const fromAttr = parseInt(priceEl?.getAttribute('data-wpd-variant-price') || '', 10);
      const attrVariantId = priceEl?.getAttribute('data-wpd-variant-id');
      const attrMatchesCurrentVariant = variant
        ? String(attrVariantId || '') === String(variant.id)
        : true;

      if (Number.isFinite(fromAttr) && fromAttr > 0 && attrMatchesCurrentVariant) {
        return fromAttr;
      }

      // If a trade-price lookup helper is available (installed by product-calculator's WPD
      // integration), consult it — mirrors product-calculator.getEffectiveCalculatorPrices lightly.
      if (
        variant &&
        typeof window.wpdGetTradePriceForVariant === 'function'
      ) {
        try {
          const tradePrice = window.wpdGetTradePriceForVariant(variant.id);
          if (Number.isFinite(tradePrice) && tradePrice > 0) return tradePrice;
        } catch (_) {}
      }

      return variant ? variant.price : 0;
    }

    getCurrentVariant() {
      const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const id = idInput?.value;
      return window.meta?.product?.variants?.find((v) => String(v.id) === String(id)) || null;
    }

    recalculate() {
      const entered = this.getEnteredFt();
      if (!Number.isFinite(entered) || entered <= 0) return;

      const pieces = window.BullnoseCalculatorMath.requiredPieces(
        entered,
        this.getRate(),
        this.getOveragePercent()
      );
      if (pieces <= 0) return;

      const qty = Math.max(pieces, this.getMinQty());
      this.setQuantity(qty);
      this.updateSummary(qty, entered);
      this.updateTotal(qty);
    }

    setQuantity(qty) {
      document.querySelectorAll('#finalquantity').forEach((el) => {
        el.value = qty;
        el.min = String(this.getMinQty());
      });

      document
        .querySelectorAll(
          '.quantity-selector-container input[name="quantity"], .product__quantity input[name="quantity"]'
        )
        .forEach((el) => {
          el.value = qty;
          el.min = String(this.getMinQty());
        });

      document.querySelectorAll('.quantity-selector-container').forEach((container) => {
        try {
          const alpine = window.Alpine?.$data?.(container);
          if (alpine && 'qty' in alpine) alpine.qty = qty;
        } catch (_) {}
      });
    }

    updateSummary(pieces, enteredFt) {
      const text = window.BullnoseCalculatorMath.summaryText(pieces, enteredFt);
      this.root.querySelectorAll('.js-bullnose-total-summary').forEach((el) => {
        el.textContent = text;
      });
    }

    updateTotal(pieces) {
      const totalCents = this.getPiecePriceCents() * pieces;
      const formatted = this.formatMoney(totalCents);
      this.root.querySelectorAll('.total-value').forEach((el) => {
        el.innerHTML = formatted;
      });
    }

    syncPiecePrice() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const retailPrice = variant.price;
      const retailFormatted = this.formatMoney(retailPrice);

      this.root.querySelectorAll('.js-bullnose-piece-price').forEach((el) => {
        const existingAttrPrice = parseInt(el.getAttribute('data-wpd-variant-price') || '', 10);
        const existingAttrVariantId = el.getAttribute('data-wpd-variant-id');
        const attrIsForCurrentVariant = String(existingAttrVariantId || '') === String(variant.id);
        const attrDiffersFromRetail =
          Number.isFinite(existingAttrPrice) &&
          existingAttrPrice > 0 &&
          Math.abs(existingAttrPrice - retailPrice) > 1;

        // If WPD has already published a trade/wholesale price for the current variant on this
        // element, leave the price attribute + displayed text alone — overwriting with the retail
        // price from window.meta would flash retail over a valid trade price.
        if (attrIsForCurrentVariant && attrDiffersFromRetail) {
          el.setAttribute(
            'data-wpd-variant-compare-at-price',
            String(variant.compare_at_price || 0)
          );
          return;
        }

        el.textContent = retailFormatted;
        el.setAttribute('data-wpd-variant-id', String(variant.id));
        el.setAttribute('data-wpd-variant-price', String(retailPrice));
        el.setAttribute(
          'data-wpd-variant-compare-at-price',
          String(variant.compare_at_price || 0)
        );
      });

      this.syncLinFtPrice();

      const entered = this.getEnteredFt();
      if (Number.isFinite(entered) && entered > 0) {
        this.recalculate();
      } else {
        // Show current piece price (could be WPD trade) as the placeholder total.
        const totalFormatted = this.formatMoney(this.getPiecePriceCents());
        this.root.querySelectorAll('.total-value').forEach((el) => {
          el.innerHTML = totalFormatted;
        });
      }
    }

    syncLinFtPrice() {
      const rate = this.getRate();
      const pieceCents = this.getPiecePriceCents();
      if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(pieceCents) || pieceCents <= 0) {
        return;
      }
      const linFtFormatted = this.formatMoney(Math.round(pieceCents * rate));
      this.root.querySelectorAll('.js-bullnose-linft-price').forEach((el) => {
        el.textContent = linFtFormatted;
      });
    }

    resetFields() {
      const input = this.root.querySelector('#bullnose_ft_need');
      if (input) input.value = '';
      const hidden = this.root.querySelector('.js-bullnose-overage-value');
      if (hidden) hidden.value = '0';
      const label = this.root.querySelector('.js-bullnose-overage-label');
      if (label) label.textContent = '0% — no overage';
      this.root.querySelectorAll('.calculator-overage-dd__opt').forEach((btn) => {
        btn.setAttribute('aria-selected', btn.getAttribute('data-value') === '0' ? 'true' : 'false');
      });
      this.root.querySelectorAll('.js-bullnose-total-summary').forEach((el) => {
        el.innerHTML = '<span>Total:</span> Enter linear feet to calculate pieces needed.';
      });
      this.setQuantity(this.getMinQty());
      this.syncPiecePrice();
    }

    toggleOverageDropdown(dd) {
      const isOpen = dd.getAttribute('data-open') === '1';
      document.querySelectorAll('[data-bullnose-overage][data-open="1"]').forEach((other) => {
        if (other !== dd) this.closeOverageDropdown(other);
      });
      if (isOpen) this.closeOverageDropdown(dd);
      else this.openOverageDropdown(dd);
    }

    openOverageDropdown(dd) {
      dd.setAttribute('data-open', '1');
      const list = dd.querySelector('.calculator-overage-dd__list');
      const trigger = dd.querySelector('.calculator-overage-dd__trigger');
      if (list) list.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    closeOverageDropdown(dd) {
      dd.setAttribute('data-open', '0');
      const list = dd.querySelector('.calculator-overage-dd__list');
      const trigger = dd.querySelector('.calculator-overage-dd__trigger');
      if (list) list.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    applyOverageOption(dd, optBtn) {
      const value = optBtn.getAttribute('data-value') || '0';
      const label = optBtn.getAttribute('data-label') || '';
      const hidden = dd.querySelector('.js-bullnose-overage-value');
      const labelEl = dd.querySelector('.js-bullnose-overage-label');
      if (hidden) hidden.value = value;
      if (labelEl) labelEl.textContent = label;
      dd.querySelectorAll('.calculator-overage-dd__opt').forEach((btn) => {
        btn.setAttribute('aria-selected', btn === optBtn ? 'true' : 'false');
      });
      this.closeOverageDropdown(dd);
      this.recalculate();
    }

    bindEvents() {
      // Bind the (mostly stateless) global document listeners once. Handlers resolve the current
      // live instance through `window.BullnoseCalculator` so re-inits on `shopify:section:load`
      // don't stack duplicate listeners on `document`.
      if (!window.__bullnoseCalculatorEventsBound) {
        window.__bullnoseCalculatorEventsBound = true;

        const withInstance = (fn) => (e) => {
          const inst = window.BullnoseCalculator;
          if (!inst || !inst.root) return;
          fn(inst, e);
        };

        document.addEventListener('input', withInstance((inst, e) => {
          if (e.target.matches('#bullnose_ft_need')) inst.recalculate();
        }));
        document.addEventListener('change', withInstance((inst, e) => {
          if (e.target.matches('#bullnose_ft_need')) inst.recalculate();
        }));

        document.addEventListener('click', withInstance((inst, e) => {
          const optBtn = e.target.closest('[data-bullnose-overage] .calculator-overage-dd__opt');
          if (optBtn) {
            const dd = optBtn.closest('[data-bullnose-overage]');
            if (dd) inst.applyOverageOption(dd, optBtn);
            return;
          }
          if (e.target.closest('[data-bullnose-overage] .calculator-overage-dd__trigger')) {
            const dd = e.target.closest('[data-bullnose-overage]');
            if (dd) {
              e.preventDefault();
              inst.toggleOverageDropdown(dd);
            }
            return;
          }
          const openDd = document.querySelector('[data-bullnose-overage][data-open="1"]');
          if (openDd && !e.target.closest('[data-bullnose-overage]')) {
            inst.closeOverageDropdown(openDd);
          }
        }));

        document.addEventListener('keydown', withInstance((inst, e) => {
          if (e.key !== 'Escape') return;
          document.querySelectorAll('[data-bullnose-overage][data-open="1"]').forEach((dd) => {
            inst.closeOverageDropdown(dd);
          });
        }));

        document.addEventListener('variant:change', withInstance((inst) => inst.onVariantChange()));
        document.addEventListener('variant:updated', withInstance((inst) => inst.onVariantChange()));

        document.addEventListener('wpd:trade-price-ready', withInstance((inst) => inst.syncPiecePrice()));
        document.addEventListener('wpd:calculator-price-synced', withInstance((inst) => inst.syncPiecePrice()));
      }

      // Section-scoped event name includes the sectionId, so it must be bound per-section — but
      // still guarded so re-inits within the same section don't stack it.
      const sectionId = this.root.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
      if (sectionId) {
        window.__bullnoseCalculatorSectionListeners = window.__bullnoseCalculatorSectionListeners || {};
        if (!window.__bullnoseCalculatorSectionListeners[sectionId]) {
          window.__bullnoseCalculatorSectionListeners[sectionId] = true;
          document.addEventListener(
            `eurus:product-page-variant-select:updated:${sectionId}`,
            () => {
              const inst = window.BullnoseCalculator;
              if (inst && inst.root) inst.onVariantChange();
            }
          );
        }
      }
    }

    onVariantChange() {
      setTimeout(() => this.resetFields(), 100);
      setTimeout(() => this.syncPiecePrice(), 200);
    }
  };

  function initBullnoseCalculator() {
    const root = document.querySelector('[data-bullnose-calculator="1"]');
    if (!root) return;

    // If a previous instance exists (e.g. after `shopify:section:load` swaps DOM), refresh its
    // root reference and re-sync rather than constructing a new instance — the global document
    // listeners in `bindEvents` are guarded and stay in place.
    const existing = window.BullnoseCalculator;
    if (existing && existing instanceof window.BullnoseCalculatorClass) {
      existing.root = root;
      existing.root.dataset.bullnoseInitialized = '1';
      existing.bindEvents(); // still called: no-op for global bind, may add new section listener
      existing.syncPiecePrice();
      return;
    }

    window.BullnoseCalculator = new window.BullnoseCalculatorClass();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBullnoseCalculator);
  } else {
    initBullnoseCalculator();
  }
  document.addEventListener('shopify:section:load', initBullnoseCalculator);
})();
